import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

class BundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.quantities = new Map();
  }

  connectedCallback() {
    // Get login status and currency code from data attributes
    this.loginStatus = this.dataset.loginStatus === "true";
    this.currencyCode = this.dataset.currencyCode || "NZD";

    // Initialize the bundle builder
    this.init();
  }

  disconnectedCallback() {
    // Cleanup event listeners if needed
    this.quantities.clear();
  }

  init() {
    this.attachQuantityListeners();
    this.updateTotal();
    this.updateAddToCartButton();
    this.interceptAddToCart();
    this.attachVariantUpdateListener();
  }

  attachQuantityListeners() {
    const quantitySelectors = this.querySelectorAll(
      "quantity-selector-component, cart-quantity-selector-component",
    );
    quantitySelectors.forEach((selector) => {
      const input = selector.querySelector('input[type="number"]');
      if (input) {
        const row = selector.closest(".bb-bundle-builder-table__row");
        const variantId = row?.dataset.variantId;

        if (variantId) {
          // Get inventory quantity from selector data attribute
          const inventoryQuantity = selector.dataset.inventoryQuantity
            ? parseInt(selector.dataset.inventoryQuantity)
            : null;
          const inventoryManaged = selector.dataset.inventoryManaged === "true";

          // Override min to 0 for bundle builder table
          input.min = 0;

          // Ensure max is set correctly based on inventory
          if (
            inventoryManaged &&
            inventoryQuantity !== null &&
            inventoryQuantity >= 0
          ) {
            input.max = inventoryQuantity;
          }

          // Set initial quantity to 0
          input.value = 0;
          this.quantities.set(variantId, 0);

          // Override the component's getCurrentValues to return min=0
          if (selector.getCurrentValues) {
            const originalGetCurrentValues =
              selector.getCurrentValues.bind(selector);
            selector.getCurrentValues = function () {
              const values = originalGetCurrentValues();
              values.min = 0; // Force min to 0
              return values;
            };
          }

          // Override updateQuantity to allow going to 0
          if (selector.updateQuantity) {
            const _originalUpdateQuantity =
              selector.updateQuantity.bind(selector);
            selector.updateQuantity = function (stepMultiplier) {
              const { quantityInput } = this.refs;
              const currentValue = parseInt(quantityInput.value) || 0;
              const step = parseInt(quantityInput.step) || 1;
              const effectiveMax = this.getEffectiveMax
                ? this.getEffectiveMax()
                : null;

              // Calculate new value, allowing it to go to 0
              const newValue = Math.min(
                effectiveMax ?? Infinity,
                Math.max(0, currentValue + step * stepMultiplier), // Use 0 instead of min
              );

              quantityInput.value = newValue.toString();

              if (this.onQuantityChange) {
                this.onQuantityChange();
              }
              if (this.updateButtonStates) {
                this.updateButtonStates();
              }

              // Update our total
              const qty = parseInt(quantityInput.value) || 0;
              const bundleTable = this.closest("bundle-builder");
              if (bundleTable) {
                bundleTable.quantities.set(variantId, qty);
                bundleTable.updateTotal();
                bundleTable.updateAddToCartButton();
              }
            };
          }

          // Override setQuantity to allow 0
          if (selector.setQuantity) {
            const _originalSetQuantity = selector.setQuantity.bind(selector);
            selector.setQuantity = function (event) {
              if (!(event.target instanceof HTMLInputElement)) return;

              event.preventDefault();
              const { quantityInput } = this.refs;
              const step = parseInt(quantityInput.step) || 1;
              const effectiveMax = this.getEffectiveMax
                ? this.getEffectiveMax()
                : null;

              // Snap to bounds, allowing 0
              const quantity = Math.min(
                effectiveMax ?? Infinity,
                Math.max(0, parseInt(event.target.value) || 0), // Use 0 instead of min
              );

              // Validate step increment (0 is always valid)
              if (quantity > 0 && (quantity - 0) % step !== 0) {
                quantityInput.value = quantity.toString();
                quantityInput.reportValidity();
                return;
              }

              quantityInput.value = quantity.toString();

              if (this.onQuantityChange) {
                this.onQuantityChange();
              }
              if (this.updateButtonStates) {
                this.updateButtonStates();
              }

              // Update our total
              const qty = parseInt(quantityInput.value) || 0;
              const bundleTable = this.closest("bundle-builder");
              if (bundleTable) {
                bundleTable.quantities.set(variantId, qty);
                bundleTable.updateTotal();
                bundleTable.updateAddToCartButton();
              }
            };
          }

          // Listen for input changes
          const handleChange = () => {
            let qty = parseInt(input.value) || 0;

            // Enforce max limit
            if (
              inventoryManaged &&
              inventoryQuantity !== null &&
              qty > inventoryQuantity
            ) {
              qty = inventoryQuantity;
              input.value = qty;
            }

            // Enforce min limit (0)
            if (qty < 0) {
              qty = 0;
              input.value = qty;
            }

            this.quantities.set(variantId, qty);
            this.updateTotal();
            this.updateAddToCartButton();
            
            // Auto-select colour option if not selected and quantity > 0
            if (qty > 0) {
              this.ensureColourOptionSelected();
            }
          };

          input.addEventListener("input", handleChange);
          input.addEventListener("change", handleChange);

          // Listen for button clicks (decrease/increase)
          const buttons = selector.querySelectorAll("button");
          buttons.forEach((button) => {
            button.addEventListener("click", () => {
              // Use setTimeout to let the quantity selector update first
              setTimeout(() => {
                let qty = parseInt(input.value) || 0;

                // Enforce max limit
                if (
                  inventoryManaged &&
                  inventoryQuantity !== null &&
                  qty > inventoryQuantity
                ) {
                  qty = inventoryQuantity;
                  input.value = qty;
                }

                // Enforce min limit (0)
                if (qty < 0) {
                  qty = 0;
                  input.value = qty;
                }

                this.quantities.set(variantId, qty);
                this.updateTotal();
                this.updateAddToCartButton();
                
                // Auto-select colour option if not selected and quantity > 0
                if (qty > 0) {
                  this.ensureColourOptionSelected();
                }
              }, 0);
            });
          });
        }
      }
    });
  }

  /**
   * Ensures that a colour option is selected if any quantity > 0.
   * If no colour option is selected, automatically selects the first available one.
   */
  ensureColourOptionSelected() {
    // Find the variant picker
    const variantPicker = this.closest("variant-picker") || 
                         document.querySelector("variant-picker");
    
    if (!variantPicker) {
      return; // No variant picker found
    }

    // Find all fieldsets (each represents an option like colour, size, etc.)
    const fieldsets = variantPicker.querySelectorAll("fieldset.variant-option");
    
    // Find the bundle builder fieldset to exclude it
    const bundleBuilderFieldset = this.closest("fieldset.variant-option");
    
    // Check each fieldset to see if it's a non-bundle-builder option
    for (const fieldset of fieldsets) {
      // Skip the bundle builder fieldset
      if (fieldset === bundleBuilderFieldset) {
        // eslint-disable-next-line no-continue
        continue;
      }

      // Check if this option has a selected value
      const hasSelectedValue = this.hasOptionSelected(
        /** @type {HTMLFieldSetElement} */ (fieldset)
      );
      
      if (!hasSelectedValue) {
        // No option selected, try to select the first available one
        const firstAvailableOption = this.findFirstAvailableOption(
          /** @type {HTMLFieldSetElement} */ (fieldset)
        );
        
        if (firstAvailableOption) {
          // Select the first available option
          this.selectOption(
            /** @type {HTMLElement} */ (firstAvailableOption),
            /** @type {HTMLElement} */ (variantPicker)
          );
          break; // Only select one option (colour)
        }
      }
    }
  }

  /**
   * Checks if an option fieldset has a selected value.
   * @param {HTMLFieldSetElement} fieldset - The fieldset to check.
   * @returns {boolean} True if an option is selected.
   */
  // eslint-disable-next-line class-methods-use-this
  hasOptionSelected(fieldset) {
    // Check for select element
    const select = fieldset.querySelector("select.variant-option__select");
    if (select) {
      // Check if a value is selected (not empty and not "Please Select")
      return select.value !== "" && select.value !== null;
    }

    // Check for radio buttons
    const checkedRadio = fieldset.querySelector(
      'input[type="radio"]:checked'
    );
    if (checkedRadio) {
      return true;
    }

    return false;
  }

  /**
   * Finds the first available option in a fieldset.
   * @param {HTMLFieldSetElement} fieldset - The fieldset to search.
   * @returns {HTMLElement | null} The first available option element.
   */
  // eslint-disable-next-line class-methods-use-this
  findFirstAvailableOption(fieldset) {
    // Check for select element
    const select = fieldset.querySelector("select.variant-option__select");
    if (select instanceof HTMLSelectElement) {
      // Find first available option (skip "Please Select" and sold out items)
      for (const option of select.options) {
        if (option.value === "" || option.value === null) {
          // eslint-disable-next-line no-continue
          continue; // Skip "Please Select"
        }
        
        // Check if option is available (not sold out)
        const isSoldOut = option.textContent?.includes("Sold out") || 
                         option.textContent?.includes("sold out");
        if (!isSoldOut) {
          return option;
        }
      }
      return null;
    }

    // Check for radio buttons
    const radios = fieldset.querySelectorAll('input[type="radio"]');
    for (const radio of radios) {
      if (radio instanceof HTMLInputElement) {
        // Check if radio is available
        const isAvailable = radio.dataset.optionAvailable !== "false" &&
                           !radio.hasAttribute("aria-disabled");
        
        if (isAvailable) {
          return radio;
        }
      }
    }

    return null;
  }

  /**
   * Selects an option and triggers the variant change event.
   * @param {HTMLElement} optionElement - The option element to select.
   * @param {HTMLElement} variantPicker - The variant picker component.
   */
  // eslint-disable-next-line class-methods-use-this
  selectOption(optionElement, variantPicker) {
    if (optionElement instanceof HTMLOptionElement) {
      // For select elements
      const select = optionElement.parentElement;
      if (select instanceof HTMLSelectElement) {
        // Set the value
        select.value = optionElement.value;
        
        // Update selected attribute
        for (const option of select.options) {
          option.removeAttribute("selected");
        }
        optionElement.setAttribute("selected", "selected");
        
        // Trigger change event
        const changeEvent = new Event("change", { bubbles: true });
        select.dispatchEvent(changeEvent);
      }
    } else if (optionElement instanceof HTMLInputElement && 
               optionElement.type === "radio") {
      // For radio buttons
      optionElement.checked = true;
      
      // Update variant picker's selected option state
      const variantPickerComponent = /** @type {any} */ (variantPicker);
      if (variantPickerComponent.updateSelectedOption) {
        variantPickerComponent.updateSelectedOption(optionElement);
      }
      
      // Trigger change event
      const changeEvent = new Event("change", { bubbles: true });
      optionElement.dispatchEvent(changeEvent);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  formatMoney(cents) {
    // Convert cents to dollars
    const dollars = cents / 100;

    // Format as decimal number and add $ symbol manually
    const formatted = new Intl.NumberFormat("en-US", {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(dollars);

    return `$${formatted}`;
  }

  updateTotal() {
    let regularTotalCents = 0; // Always use regular price for total

    this.quantities.forEach((quantity, variantId) => {
      if (quantity > 0) {
        const row = this.querySelector(`[data-variant-id="${variantId}"]`);
        if (row) {
          const priceCents = parseInt(row.dataset.price) || 0;

          // Always use regular price for total
          regularTotalCents += priceCents * quantity;
        }
      }
    });

    // Update total price (always show regular price)
    const totalElement = this.querySelector('[ref="totalPrice"]');
    if (totalElement) {
      totalElement.textContent = this.formatMoney(regularTotalCents);
    }

    // Update RRP price (regular total, shown for logged in users)
    const rrpPriceElement = this.querySelector('[ref="rrpPrice"]');
    if (rrpPriceElement) {
      rrpPriceElement.textContent = this.formatMoney(regularTotalCents);
    }

    // Calculate bbb&me total (regular total * 0.7)
    const bbbmeTotalCents = regularTotalCents * 0.7;

    // Update guest afterpay price (regular total / 4)
    const guestAfterpayPriceElement = this.querySelector(
      '[ref="guestAfterpayPrice"]',
    );
    if (guestAfterpayPriceElement) {
      const guestAfterpayCents = regularTotalCents / 4;
      guestAfterpayPriceElement.textContent =
        this.formatMoney(guestAfterpayCents);

      // Show/hide guest afterpay section based on total
      const guestAfterpaySection = guestAfterpayPriceElement.closest(
        ".bb-bundle-builder-table__afterpay",
      );
      if (guestAfterpaySection) {
        if (regularTotalCents > 0) {
          guestAfterpaySection.classList.remove("hidden");
        } else {
          guestAfterpaySection.classList.add("hidden");
        }
      }
    }

    // Update member afterpay price (bbb&me total / 4)
    const memberAfterpayPriceElement = this.querySelector(
      '[ref="memberAfterpayPrice"]',
    );
    if (memberAfterpayPriceElement) {
      const memberAfterpayCents = bbbmeTotalCents / 4;
      memberAfterpayPriceElement.textContent =
        this.formatMoney(memberAfterpayCents);

      // Show/hide member afterpay section based on total
      const memberAfterpaySection = memberAfterpayPriceElement.closest(
        ".bb-bundle-builder-table__afterpay",
      );
      if (memberAfterpaySection) {
        if (regularTotalCents > 0) {
          memberAfterpaySection.classList.remove("hidden");
        } else {
          memberAfterpaySection.classList.add("hidden");
        }
      }
    }

    // Update yourTotalPrice (same as bbbmePrice)
    const yourTotalPriceElement = this.querySelector('[ref="yourTotalPrice"]');
    if (yourTotalPriceElement) {
      yourTotalPriceElement.textContent = this.formatMoney(bbbmeTotalCents);
    }

    // Update bbb&me price or savings
    if (this.loginStatus) {
      // If logged in, show savings (regular total - bbb&me total)
      const bbbmeSaveElement = this.querySelector('[ref="bbbmeSave"]');
      if (bbbmeSaveElement) {
        const savingsCents = regularTotalCents - bbbmeTotalCents;
        bbbmeSaveElement.textContent = this.formatMoney(savingsCents);
      }
    } else {
      // If not logged in, show bbb&me price (regular total * 0.7)
      const bbbmePriceElement = this.querySelector('[ref="bbbmePrice"]');
      if (bbbmePriceElement) {
        bbbmePriceElement.textContent = this.formatMoney(bbbmeTotalCents);
      }
    }
  }

  updateAddToCartButton() {
    // Check if any quantity is greater than 0
    let hasQuantity = false;
    this.quantities.forEach((quantity) => {
      if (quantity > 0) {
        hasQuantity = true;
      }
    });

    // Find the add to cart button in the product form
    // Look for the button with ref="addToCartButton" within product-form-component
    const productForm =
      this.closest("product-form-component") ||
      document.querySelector("product-form-component");

    if (productForm) {
      const addToCartButton = productForm.querySelector(
        '[ref="addToCartButton"]',
      );
      if (addToCartButton instanceof HTMLButtonElement) {
        // Disable if no quantity is selected
        addToCartButton.disabled = !hasQuantity;

        // Also check if the button is inside add-to-cart-component
        const addToCartComponent = addToCartButton.closest(
          "add-to-cart-component",
        );
        if (addToCartComponent) {
          // Update aria attributes for accessibility
          if (!hasQuantity) {
            addToCartButton.setAttribute("aria-disabled", "true");
          } else {
            addToCartButton.removeAttribute("aria-disabled");
          }
        }
      }
    }
  }

  getBundleItems() {
    // Get all items with quantity > 0
    const items = [];
    this.quantities.forEach((quantity, variantId) => {
      if (quantity > 0) {
        const row = this.querySelector(`[data-variant-id="${variantId}"]`);
        if (row) {
          items.push({
            id: variantId,
            quantity,
          });
        }
      }
    });
    return items;
  }

  attachVariantUpdateListener() {
    // Listen for variant updates to refresh max quantities
    const target = this.closest(".shopify-section, dialog, product-card");
    if (target) {
      target.addEventListener("variant:update", (event) => {
        this.handleVariantUpdate(event);
      });
    }
  }

  handleVariantUpdate(event) {
    // Check if this update is for our product
    const { productId } = this.dataset;
    const { detail } = event;
    const { data } = detail || {};

    if (data?.productId && data.productId !== productId) {
      return; // Not our product, ignore
    }

    // Get the new HTML from the event
    const newHtml = data?.html;
    if (!newHtml) {
      return;
    }

    // Find the new bundle builder table in the updated HTML
    const newTable = newHtml.querySelector(
      "bundle-builder, .bb-bundle-builder-table",
    );
    if (!newTable) {
      return; // Table not in the update, might be a different section
    }

    // Update max quantities for each row based on new variant data
    // Match rows by option value (item name) since variant ID changes when colour changes
    const rows = this.querySelectorAll(".bb-bundle-builder-table__row");
    rows.forEach((row) => {
      const { optionValue } = row.dataset;
      if (!optionValue) return;

      // Find the corresponding row in the new HTML by option value
      const newRow = newTable.querySelector(
        `[data-option-value="${optionValue}"]`,
      );
      if (!newRow) return;

      // Get the new variant ID
      const newVariantId = newRow.dataset.variantId;
      if (!newVariantId) return;

      // If variant ID changed, update our quantities map
      const oldVariantId = row.dataset.variantId;
      if (oldVariantId && oldVariantId !== newVariantId) {
        // Transfer quantity from old variant to new variant if it exists
        const oldQuantity = this.quantities.get(oldVariantId) || 0;
        if (oldQuantity > 0) {
          this.quantities.set(newVariantId, oldQuantity);
          this.quantities.delete(oldVariantId);
        } else {
          // Ensure new variant is in the map
          this.quantities.set(newVariantId, 0);
        }

        // Update the row's variant ID
        row.dataset.variantId = newVariantId;
      }

      // Get the new quantity selector from the new row
      const newSelector = newRow.querySelector(
        "quantity-selector-component, cart-quantity-selector-component",
      );
      if (!newSelector) return;

      // Get new inventory data
      const newInventoryQuantity = newSelector.dataset.inventoryQuantity
        ? parseInt(newSelector.dataset.inventoryQuantity)
        : null;
      const newInventoryManaged =
        newSelector.dataset.inventoryManaged === "true";

      // Find the current quantity selector in our table
      const currentSelector = row.querySelector(
        "quantity-selector-component, cart-quantity-selector-component",
      );
      if (!currentSelector) return;

      const currentInput = currentSelector.querySelector(
        'input[type="number"]',
      );
      if (!currentInput) return;

      // Update variant ID on selector if it changed
      if (oldVariantId && oldVariantId !== newVariantId) {
        currentSelector.dataset.variantId = newVariantId;
      }

      // Update the data attributes
      currentSelector.dataset.inventoryQuantity =
        newInventoryQuantity !== null ? newInventoryQuantity.toString() : "";
      currentSelector.dataset.inventoryManaged = newInventoryManaged.toString();

      // Update the input max value
      if (
        newInventoryManaged &&
        newInventoryQuantity !== null &&
        newInventoryQuantity >= 0
      ) {
        currentInput.max = newInventoryQuantity;
      } else {
        currentInput.removeAttribute("max");
      }

      // Get current quantity value (use new variant ID if it changed)
      const currentVariantId =
        oldVariantId && oldVariantId !== newVariantId
          ? newVariantId
          : oldVariantId || newVariantId;
      const currentQuantity =
        this.quantities.get(currentVariantId) ||
        parseInt(currentInput.value) ||
        0;

      // If current quantity exceeds new max, clamp it
      if (
        newInventoryManaged &&
        newInventoryQuantity !== null &&
        currentQuantity > newInventoryQuantity
      ) {
        currentInput.value = newInventoryQuantity.toString();
        this.quantities.set(newVariantId, newInventoryQuantity);
        this.updateTotal();
        this.updateAddToCartButton();
      } else {
        // Update input value to match our quantities map
        currentInput.value = currentQuantity.toString();
      }

      // Update the row's data attributes for price calculation
      const newPrice = newRow.dataset.price;
      const newBbbPrice = newRow.dataset.bbbPrice;
      const newIsOnSale = newRow.dataset.isOnSale;

      if (newPrice) row.dataset.price = newPrice;
      if (newBbbPrice) row.dataset.bbbPrice = newBbbPrice;
      if (newIsOnSale !== undefined) row.dataset.isOnSale = newIsOnSale;

      // Recalculate total with updated prices
      this.updateTotal();
    });

    // Update add to cart button state after variant update
    // If no quantity > 0, button should be disabled
    this.updateAddToCartButton();
  }

  interceptAddToCart() {
    // Find the product form component
    const productForm =
      this.closest("product-form-component") ||
      document.querySelector("product-form-component");

    if (productForm && !productForm.__bundleIntercepted) {
      productForm.__bundleIntercepted = true;

      // Prevent add-to-cart-component from animating on click for bundle builder
      const addToCartButton = productForm.querySelector(
        '[ref="addToCartButton"]',
      );
      const addToCartComponent = addToCartButton?.closest(
        "add-to-cart-component",
      );

      // Store reference to BundleBuilder instance for use in handleClick
      const bundleBuilderInstance = this;

      if (addToCartComponent && !addToCartComponent.__bundleIntercepted) {
        addToCartComponent.__bundleIntercepted = true;

        // Store original handleClick
        const originalHandleClick =
          addToCartComponent.handleClick?.bind(addToCartComponent);

        // Override handleClick to prevent animation for bundle builder
        if (originalHandleClick) {
          addToCartComponent.handleClick = (event) => {
            // Check if this is a bundle builder scenario
            const bundleItems = bundleBuilderInstance.getBundleItems();

            if (bundleItems.length > 0) {
              // Don't animate for bundle builder - we'll handle it after success
              // Just validate the form, don't trigger animations
              const form = addToCartComponent.closest("form");
              if (form?.checkValidity()) {
                // Form is valid, but we don't want to animate here
                // The form submission will be handled by interceptAddToCart
              }
            } else {
              // Not bundle builder, use original behavior
              return originalHandleClick(event);
            }
          };
        }
      }

      // Store original handleSubmit
      const originalHandleSubmit = productForm.handleSubmit.bind(productForm);

      // Override handleSubmit
      productForm.handleSubmit = async (event) => {
        // Check if this is a bundle builder table scenario
        const bundleItems = this.getBundleItems();

        // If we have bundle items, handle them specially
        if (bundleItems.length > 0) {
          event.preventDefault();

          // Disable button during processing
          // Use the button from outer scope if available, otherwise query again
          const addToCartButtonInner =
            addToCartButton ||
            productForm.querySelector('[ref="addToCartButton"]');
          const addToCartButtonContainer =
            productForm.refs?.addToCartButtonContainer;
          if (addToCartButtonContainer) {
            addToCartButtonContainer.disable();
          } else if (addToCartButtonInner) {
            addToCartButtonInner.disabled = true;
          }

          try {
            // Get sections to update
            const cartItemsComponents = document.querySelectorAll(
              "cart-items-component",
            );
            const sectionIds = [];
            cartItemsComponents.forEach((item) => {
              if (item instanceof HTMLElement && item.dataset.sectionId) {
                sectionIds.push(item.dataset.sectionId);
              }
            });

            // Add items one by one using the same format as product-form
            const cartAddUrl =
              window.Theme?.routes?.cart_add_url || "/cart/add.js";
            let totalItemCount = 0;
            let lastVariantId = null;
            let sections = {};
            let allItemsAdded = true;

            // Add all items sequentially
            for (const item of bundleItems) {
              const formData = new FormData();
              formData.append("id", item.id);
              formData.append("quantity", item.quantity);
              if (sectionIds.length > 0) {
                formData.append("sections", sectionIds.join(","));
              }

              const fetchCfg = fetchConfig("javascript", { body: formData });

              const response = await fetch(cartAddUrl, {
                ...fetchCfg,
                headers: {
                  ...fetchCfg.headers,
                  Accept: "application/json",
                },
              });

              const result = await response.json();

              if (result.status) {
                // Error occurred - stop adding items
                allItemsAdded = false;
                const errorMessage =
                  result.message || "Failed to add item to cart";
                productForm.dispatchEvent(
                  new CartErrorEvent(
                    productForm.id || "",
                    errorMessage,
                    result.description,
                    result.errors,
                  ),
                );
                throw new Error(errorMessage);
              }

              totalItemCount += item.quantity;
              lastVariantId = item.id;
              if (result.sections) {
                sections = { ...sections, ...result.sections };
              }
            }

            // Only proceed if all items were successfully added
            if (allItemsAdded) {
              // Dispatch cart add event
              productForm.dispatchEvent(
                new CartAddEvent({}, lastVariantId, {
                  source: "bundle-builder-table",
                  itemCount: totalItemCount,
                  productId: productForm.dataset.productId,
                  sections,
                }),
              );

              // Don't reset quantities - keep them as they are
              // Total and button state remain unchanged

              // Wait a moment for cart to update, then show success message and animation
              setTimeout(() => {
                // Show success message
                const { liveRegion } = productForm.refs || {};
                if (liveRegion) {
                  const addedText =
                    window.Theme?.translations?.added || "Added to cart";
                  liveRegion.textContent = addedText;
                  setTimeout(() => {
                    liveRegion.textContent = "";
                  }, 2000);
                }

                // Show success animation after all items are successfully added
                const addToCartComponentInner = addToCartButtonInner?.closest(
                  "add-to-cart-component",
                );
                if (addToCartComponentInner) {
                  // Animate button text change
                  if (addToCartComponentInner.animateAddToCart) {
                    addToCartComponentInner.animateAddToCart();
                  }

                  // Trigger fly to cart animation if enabled
                  const animationEnabled =
                    addToCartComponentInner.dataset.addToCartAnimation ===
                    "true";
                  if (animationEnabled) {
                    // Manually create fly-to-cart animation (since #animateFlyToCart is private)
                    const cartIcon = document.querySelector(
                      ".header-actions__cart-icon",
                    );
                    const image =
                      addToCartComponentInner.dataset.productVariantMedia;

                    if (cartIcon && addToCartButtonInner && image) {
                      // Create fly-to-cart element directly
                      const flyToCartElement =
                        document.createElement("fly-to-cart");
                      flyToCartElement.style.setProperty(
                        "background-image",
                        `url(${image})`,
                      );
                      flyToCartElement.source = addToCartButtonInner;
                      flyToCartElement.destination = cartIcon;
                      document.body.appendChild(flyToCartElement);
                    }
                  }
                }

                // Re-enable button after animation starts
                // Quantities are not reset, so button should remain enabled
                if (addToCartButtonContainer) {
                  addToCartButtonContainer.enable();
                } else if (addToCartButtonInner) {
                  addToCartButtonInner.disabled = false;
                }
              }, 100);
            }
          } catch (error) {
            console.error("Error adding bundle items to cart:", error);

            // Re-enable button
            if (addToCartButtonContainer) {
              addToCartButtonContainer.enable();
            } else if (addToCartButtonInner) {
              addToCartButtonInner.disabled = false;
            }

            // Show error message
            const liveRegion = productForm.refs?.liveRegion;
            const addToCartTextError = productForm.refs?.addToCartTextError;
            const errorMessage = error.message || "Failed to add items to cart";

            if (addToCartTextError) {
              addToCartTextError.classList.remove("hidden");
              const textNode = addToCartTextError.childNodes[2];
              if (textNode) {
                textNode.textContent = errorMessage;
              } else {
                const newTextNode = document.createTextNode(errorMessage);
                addToCartTextError.appendChild(newTextNode);
              }

              setTimeout(() => {
                if (addToCartTextError) {
                  addToCartTextError.classList.add("hidden");
                }
              }, 3000);
            }

            if (liveRegion) {
              liveRegion.textContent = errorMessage;
              setTimeout(() => {
                liveRegion.textContent = "";
              }, 3000);
            }
          }
        } else {
          // No bundle items, use original handler
          return originalHandleSubmit(event);
        }
      };
    }
  }
}

if (!customElements.get("bundle-builder")) {
  customElements.define("bundle-builder", BundleBuilder);
}